/**
 * The Value board's facts — what each enquiry was quoted at, and what it cost us (Akhil,
 * 2026-09-17: "in the analysis page where is the price for each enquiry").
 *
 * `analytics.get_pipeline_analytics` carries counts, rates and ages and no money at all, so the
 * value of an enquiry is read off its quotation — `/api/resource/Quotation` list rows — and
 * summed here, the way `aggregate.ts` builds the funnel from enquiry records. Same caveat: this
 * reads every quotation, and the fix when that stops scaling is a server aggregate (filed), not
 * pagination here.
 *
 * ---- Currencies ----
 *
 * A quotation is priced in the customer's currency; the board compares enquiries, so every figure
 * is ALSO restated into the company's through the document's own `exchange_rate` (company →
 * quotation, the rate the document was priced at — not today's). `total_po_cost` is stored in the
 * company currency already (`lib/derivedFigures.ts`), so margin is company-currency arithmetic.
 *
 * `grand_total` is the server's own figure. Until `D40` lands it omits discount and taxes — the
 * board says "as stored on the quotation" for that reason.
 */

import type { DateRange } from './range'
import type { Money } from '../types'

export interface WireQuotationValueRow {
  name: string
  enquiry?: string | null
  customer?: string | null
  customer_name?: string | null
  status?: string | null
  transaction_date?: string | null
  customer_currency?: string | null
  exchange_rate?: number | null
  grand_total?: number | null
  net_total?: number | null
  total_po_cost?: number | null
}

export interface ValueFact {
  quotation: string
  enquiry: string
  customer: string
  customer_name: string
  /** The quotation doctype's own vocabulary: Draft · Quoted · Won · Rejected · Cancelled. */
  status: string
  date: string
  /** As quoted to the customer, in their currency. */
  quoted: Money
  /** The same, in the company's currency. */
  quoted_base: Money
  /** What the goods cost us, company currency. */
  po_cost: Money
  margin: Money
  /** Margin over PO cost, in percent; `null` when there is no cost to measure against. */
  margin_pct: number | null
}

const round2 = (n: number) => Math.round(n * 100) / 100
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0)
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))

export function toValueFacts(rows: readonly WireQuotationValueRow[], baseCurrency: string): ValueFact[] {
  return rows.map((row) => {
    const currency = str(row.customer_currency) || baseCurrency
    const rate = num(row.exchange_rate) > 0 ? num(row.exchange_rate) : 1
    const quoted = num(row.grand_total)
    const quotedBase = currency === baseCurrency ? quoted : round2(quoted / rate)
    const poCost = num(row.total_po_cost)
    const margin = round2(quotedBase - poCost)
    return {
      quotation: row.name,
      enquiry: str(row.enquiry),
      customer: str(row.customer),
      customer_name: str(row.customer_name) || str(row.customer),
      status: str(row.status),
      date: str(row.transaction_date),
      quoted: { amount: quoted, currency },
      quoted_base: { amount: quotedBase, currency: baseCurrency },
      po_cost: { amount: poCost, currency: baseCurrency },
      margin: { amount: margin, currency: baseCurrency },
      margin_pct: poCost > 0 ? round2((margin / poCost) * 100) : null,
    }
  })
}

export function filterValuesByRange(facts: readonly ValueFact[], range: DateRange): ValueFact[] {
  if (!range.from && !range.to) return [...facts]
  return facts.filter((f) => f.date !== '' && (!range.from || f.date >= range.from) && (!range.to || f.date <= range.to))
}

export interface ValueSummary {
  count: number
  won_count: number
  quoted_total: Money
  won_total: Money
  lost_total: Money
  /** Draft + Quoted — still in play. */
  open_total: Money
  /** Margin over the quotations that HAVE a PO cost — the rest have nothing to measure against. */
  margin_total: Money
  margin_pct: number | null
  costed_count: number
}

export function summarizeValues(facts: readonly ValueFact[], baseCurrency: string): ValueSummary {
  const sum = (pick: (f: ValueFact) => boolean, of: (f: ValueFact) => number) => round2(facts.filter(pick).reduce((s, f) => s + of(f), 0))
  const quotedBase = (f: ValueFact) => f.quoted_base.amount
  const won = (f: ValueFact) => f.status === 'Won'
  const lost = (f: ValueFact) => f.status === 'Rejected' || f.status === 'Cancelled'
  const open = (f: ValueFact) => !won(f) && !lost(f)
  // Only where a PO cost is recorded: a quotation without one is not all profit, it is
  // unmeasured (older documents predate `total_po_cost` being written).
  const costed = (f: ValueFact) => f.po_cost.amount > 0
  const cost = sum(costed, (f) => f.po_cost.amount)
  const marginTotal = sum(costed, (f) => f.margin.amount)
  const money = (amount: number): Money => ({ amount, currency: baseCurrency })
  return {
    count: facts.length,
    won_count: facts.filter(won).length,
    quoted_total: money(sum(() => true, quotedBase)),
    won_total: money(sum(won, quotedBase)),
    lost_total: money(sum(lost, quotedBase)),
    open_total: money(sum(open, quotedBase)),
    margin_total: money(marginTotal),
    margin_pct: cost > 0 ? round2((marginTotal / cost) * 100) : null,
    costed_count: facts.filter(costed).length,
  }
}
